import type { SessionEntry } from '@/api/vendo';
import apiClient from './_axios';

export interface DashboardStats {
   totalSales: number;
   netProfit: number;
   subscriptionSales: number;
   commissionSales: number;
   customerPayable: number;
   pendingPayout: number;
   settledPayouts: number;
   totalDisbursementFees: number;
   totalUsers: number;
   totalStores: number;
   totalTransactions: number;
   totalSubscriptions: number;
}

export interface AdminUser {
   _id: string;
   email: string;
   createdAt: string;
   totalSales: number;
   totalReceivedPayout: number;
   totalAvailablePayout: number;
   storesCount: number;
   totalVAT: number;
   totalXenditFee: number;
   totalPlatformFee: number;
   totalSubscription: number;
   verified?: boolean;
}

export interface AdminStore {
   _id: string;
   storename: string;
   location: string;
   logo: string;
   storeDomain: string;
   designTheme: string;
   owner: { _id: string; email: string; name: string };
   totalSales: number;
   totalTransactions: number;
   totalPayouts: number;
   payable: number;
   subscription?: { plan: 'pro' | 'free'; end: string | null };
   createdAt: string;
}

/** Real emails sent this month vs the Mailtrap plan limit; null when Mailtrap can't be reached. */
export interface EmailUsage {
   sent: number;
   limit: number;
   plan: string;
   cycleStart: string | null;
   cycleEnd: string | null;
}
export const getEmailUsage = async (): Promise<EmailUsage | null> => {
   const response = await apiClient.get('/admin/email-usage');
   return response.data.usage ?? null;
};

/** The Admin Panel's "Server load" card: how busy the server, the database and the fleet's callers are. */
export interface ServerLoad {
   updatedAt: string | null;
   status: 'healthy' | 'busy' | 'overloaded' | 'no-data';
   cores: Array<{ proc: string; busyPct: number; freezeMs: number }>;
   freezeMaxTodayMs: number;
   series: Array<{
      t: string;
      busyPct: number;
      busyMaxPct: number;
      dbPerSec: number;
      nodemcuPerMin: number;
      routerPerMin: number;
      appPerMin: number;
   }>;
   requests: { router: number; nodemcu: number; app: number; total: number };
   noisiest: Array<{ id: string; name: string; kind: 'router' | 'nodemcu'; perMin: number; normalPerMin: number; flooding: boolean }>;
   flooding: number;
   db: {
      perSec: number;
      peakPerSec24h: number;
      avgMs: number;
      limitPerSec: number;
      plan: string;
      planGb: number;
      dataMb: number | null;
      growthMbPerDay: number | null;
      daysToFull: number | null;
      top: Array<{ name: string; mb: number }>;
   };
   host: { memTotalMb: number; memAvailMb: number; diskTotalGb: number; diskFreeGb: number } | null;
   security: { blocked: number; failedLogins: number; topIp: { ip: string; n: number } | null };
   fleet: { routersOnline: number; devicesOnline: number; total: number } | null;
}
export const getServerLoad = async (): Promise<ServerLoad> => {
   const response = await apiClient.get('/admin/server-load');
   return response.data;
};

export const getAdminDashboard = async (): Promise<DashboardStats> => {
   const response = await apiClient.get('/admin/dashboard');
   return response.data;
};

export const getAdminAccounts = async () => {
   // AdminPanel paginates client-side over the full list; limit=100 capped
   // the Users tab at 7 pages while 215+ accounts exist.
   const response = await apiClient.get('/admin/accounts?limit=5000');
   return response.data;
};

export interface AdminChartData {
   date: string;
   revenue: number;
   commission: number;
   subscription: number;
   netToSellers: number;
   transactions: number;
}

export const getAdminSalesChart = async (from: string, to: string) => {
   const params = new URLSearchParams();
   if (from) params.append('from', from);
   if (to) params.append('to', to);
   const response = await apiClient.get(`/admin/sales-chart?${params.toString()}`);
   return response.data;
};

export const getAdminStores = async () => {
   const response = await apiClient.get('/admin/stores');
   return response.data;
};

export interface AdminTransaction {
   _id: string;
   store: string;
   owner: { _id: string; email: string; name: string };
   customer: string;
   amount: number;
   platformFee: number;
   xenditFee: number;
   vat: number;
   netAmount: number;
   voucherCode: string;
   status: string;
   smsStatus: string;
   updatedAt: string;
}

export interface AdminSubscription {
   _id: string;
   store: string;
   owner: { _id: string; email: string; name: string };
   start: string;
   end: string;
   active: boolean;
   createdAt: string;
   months: number;
   amount: number;
}

export const getAdminTransactions = async () => {
   const response = await apiClient.get('/admin/transactions');
   return response.data;
};

export const getAdminSubscriptions = async () => {
   const response = await apiClient.get('/admin/subscriptions');
   return response.data;
};

export interface AdminPayout {
   _id: string;
   owner: { _id: string; email: string; name: string };
   amount: number;
   disbursementFee: number;
   netAmount: number;
   payoutMethod: string;
   payoutAccount: string;
   payoutAccountNumber: number;
   status: string;
   createdAt: string;
   /** Owner-level available balance at fetch time: successful net sales −
    * settled payout amount (same rule the payout gate uses). */
   ownerAvailable?: number;
}

export const getAdminPayouts = async () => {
   const response = await apiClient.get('/admin/payouts');
   return response.data;
};

export const settleAdminPayout = async (id: string) => {
   const response = await apiClient.put(`/admin/payout-settle?id=${id}`);
   return response.data;
};

export const rejectAdminPayout = async (id: string) => {
   const response = await apiClient.put(`/admin/payout-reject?id=${id}`);
   return response.data;
};

export const adminSetStorePlan = async (payload: {
   storeId: string;
   subscribe: '1' | '3' | '6' | '12';
   recordPayment: boolean;
}) => {
   const response = await apiClient.post('/admin/set-plan', payload);
   return response.data;
};

export const adminDeleteUser = async (id: string) => {
   const response = await apiClient.delete(`/admin/accounts/${id}`);
   return response.data;
};

// ─────────────────────────────────────────────────────────────────────
// Tier 3 — Distributor applications, credit purchases, system settings,
// inquiry reversal (admin endpoints).
// ─────────────────────────────────────────────────────────────────────

export type ApplicationStatusServer = 'pending' | 'approved' | 'rejected';

export interface AdminQualifyingAnswers {
   hasPisoWifiExperience: boolean;
   hasMikrotikKnowledge: boolean;
   isPriorDistributor: boolean;
   hasPhysicalLocation: boolean;
   willProvideSupport: boolean;
   hasWaitingCustomers: boolean;
   willSellInOwnRegion: boolean;
   hasOnlinePresence: boolean;
   canCommitMonthly: boolean;
   willDoOwnSetup: boolean;
}

export interface AdminDistributorApplication {
   _id: string;
   applicant: { _id: string; email: string; name?: string } | string;
   status: ApplicationStatusServer;
   displayName: string;
   businessName: string;
   region: string;
   city: string;
   contactPhone: string;
   facebookUrl: string;
   govIdUrl: string;
   mayorsPermitUrl: string;
   bir2303Url: string;
   proofOfAddressUrl: string;
   qualifyingAnswers: AdminQualifyingAnswers;
   defaultPricePerCredit: number;
   // Advance credit-bundle purchase captured at apply time (wizard step 2).
   // The buy executes on approval; verify the payment proof before approving.
   bundleName?: string;
   bundleCredits?: number;
   bundleAmount?: number;
   paymentMethod?: 'gcash' | 'bank-transfer';
   paymentProofUrl?: string;
   referenceNumber?: string;
   paidAt?: string;
   approvedAt?: string;
   approvedBy?: string;
   rejectedAt?: string;
   rejectedBy?: string;
   rejectionReason?: string;
   partnerCreated?: string;
   createdAt: string;
   updatedAt: string;
}

export type PurchaseStatusServer = 'pending' | 'payment-submitted' | 'approved' | 'rejected';
// Bundle name (free-form). Was previously a fixed 6-tier enum; admin
// can now add/rename/delete bundles via Settings → Credit Package Bundles.
export type PackageTierServer = string;

export interface AdminCreditPurchase {
   _id: string;
   buyer: { _id: string; email: string; name?: string } | string;
   tier: PackageTierServer;
   credits: number;
   amount: number;
   pricePerCredit: number;
   status: PurchaseStatusServer;
   paymentMethod?: 'gcash' | 'bank-transfer';
   paymentProofUrl?: string;
   referenceNumber?: string;
   paidAt?: string;
   proofSubmittedAt?: string;
   approvedAt?: string;
   approvedBy?: string;
   rejectedAt?: string;
   rejectedBy?: string;
   rejectionReason?: string;
   createdAt: string;
   updatedAt: string;
}

export interface BankAccountEntry {
   bankName: string;
   accountNumber: string;
   accountName: string;
}

export interface GcashAccountEntry {
   number: string;
   name: string;
}

// Admin-editable credit package bundle. Name is the display label AND
// the identifier denormalized into CreditPurchase.tier at initiate-time.
// Free-form (admin can add/rename/delete bundles).
export interface CreditPackageBundleEntry {
   name: string;
   credits: number;
   amount: number;
   icon?: string;
   savings?: number;
   // Loyalty points a distributor earns buying this bundle (points system).
   points?: number;
   // Extra promo credits granted on top of `credits`, highlighted in marketplace.
   bonusCredits?: number;
   isActive: boolean;
}

export interface MaintenanceSettings {
   active: boolean;
   message: string;
   endsAt: string | null;
   durationHours: number;
   excludedStoreDomains: string[];
}

export interface SystemSettings {
   bankAccounts: BankAccountEntry[];
   gcashAccounts: GcashAccountEntry[];
   creditPackages: CreditPackageBundleEntry[];
   paymentInstructions?: string;
   maintenance?: MaintenanceSettings;
   monthlyReleaseLimit?: number;
   resellerPointsPerCredit?: number;
   referralPointsPerCredit?: number;
   pointsPerCredit?: number;
   distributorPointsPerCredit?: number;
   /**
    * What SmartFi charges an ISP operator per active subscriber per month,
    * banded by how many they have.
    *
    * 🔴 IN PESOS, PAID IN POINTS. A credit is ₱500 and cannot express a ₱370
    * bill, so operators pay in points — but the bands hold MONEY, because the
    * conversion rate is editable and moving it must never silently change what
    * an operator pays. Flat per band: 15 subscribers is 15 × the 11–30 price,
    * not a graduated sum, because an operator has to check their own bill.
    */
   ispPricingTiers?: { upTo: number; pricePerAccount: number }[];
   /**
    * How many points one credit becomes for ISP billing — SEPARATE from
    * `pointsPerCredit`, which the loyalty system uses to convert points back
    * into credits (Ray, 2026-09-01: the ISP rate *"cant affect the original
    * system of credit to points conversion"*).
    *
    * 🔴 The server refuses a value above `pointsPerCredit`: points convert back
    * to credits at that rate, so a higher ISP rate would let anyone turn one
    * credit into more than one credit, over and over.
    */
   ispPointsPerCredit?: number;
   // System-controlled credit prices (no per-distributor pricing).
   baseCreditPrice?: number;
   resellerPricePerCredit?: number;
   // Free-trial length in days granted on first device activation.
   trialDays?: number;
   // Master switch for the automatic config OTA cron. Off = config only rolls
   // out when an admin clicks Update/Roll-out. Defaults off server-side.
   autoOtaEnabled?: boolean;
   // Master switch for the Xendit merchant (GCash). Off = every customer
   // portal hides the e-wallet listing and shows a "temporarily unavailable"
   // banner; coins and printable vouchers are unaffected. Defaults off.
   xenditEnabled?: boolean;
   /** Smallest online payment offered anywhere, in pesos (platform-wide). */
   onlineMinAmount?: number;
   // One row per online payment channel, under the master switch above.
   // The server sends every channel it knows, with the label, Xendit's own
   // amount floor and whether a walk-in on a vendo can reach it — save back
   // only `code`, `enabled`, `feePct` and `feeFlat`; the rest is read-only.
   paymentChannels?: PaymentChannel[];
}

export interface PaymentChannel {
   /** Stable code, e.g. 'MAYA'. */
   code: string;
   /** Customer-facing name, e.g. 'Maya'. */
   label: string;
   enabled: boolean;
   /** What Xendit takes, in percent — from OUR rate card, so editable. */
   feePct: number;
   /** Flat peso part on top (over-the-counter has one). */
   feeFlat: number;
   /** Xendit's floor in pesos: a cheaper plan can never use this channel. */
   minAmount: number;
   /** Xendit's ceiling in pesos. 0 = none. */
   maxAmount: number;
   /**
    * Whether a walk-in paying from a vendo's captive portal can reach it.
    * False = the channel only works where the customer already has internet
    * (the voucher store, an operator paying a subscription), because their
    * phone can only reach hosts the router whitelists until they have paid.
    */
   walkInSafe: boolean;   /** Who settles it. Absent = 'xendit'. */
   provider?: 'xendit' | 'paymongo';
   /** PayMongo can take this channel on the portal (QR Ph only today). */
   paymongo?: boolean;
}

export interface AdminVendoDevice {
   _id: string;
   name: string;
   deviceId: string;
   portalSlug: string;
   status: 'online' | 'offline' | 'error';
   lastHeartbeat: string | null;
   // Server-derived from router.lastRestOkAt + cluster-root inheritance.
   // Drives the RouterPill — independent from `status` (NodeMCU heartbeat).
   routerStatus?: 'connected' | 'agent-down' | 'disconnected' | 'unprovisioned';
   /** Server-derived: an update/reboot is in progress (2d). */
   updateState?: 'idle' | 'updating' | 'rebooting';
   owner: { _id: string; name?: string; email: string } | string;
   licenseStatus: 'unactivated' | 'trial' | 'active' | 'expired' | 'lifetime';
   trialEndsAt: string | null;
   licenseEndsAt: string | null;
   /** Registration progress — anything other than 'done' is still pending. */
   setupStatus?: 'flashing' | 'configuring-router' | 'connecting-portal' | 'done';
   /**
    * Coin-box telemetry. Since 2026-09-28 the fleet list is a PROJECTION
    * (ADMIN_FLEET_LIST_FIELDS on the server): a field the table starts reading
    * must be added there too, or it renders blank. The Settings modal gets the
    * full document from getAdminVendoDevice.
    */
   nodeMcu?: {
      firmwareVersion?: string;
      mac?: string;
      lastHeartbeat?: string | null;
      /** 'ethernet' boxes have no radio — their wifi fields are all 0. */
      connMode?: 'ethernet' | 'wifi';
      /** dBm, always negative. 0 means "wired", never "perfect". */
      wifiRssi?: number;
      wifiChannel?: number;
      /** ⚠️ Counter in RAM — resets to 0 on every reboot. Only a RATE is safe. */
      wifiReconnects?: number;
      /** The specific antenna the box attached to. */
      bssid?: string;
      /** Seconds since the box booted. Required to read wifiReconnects. */
      uptimeSec?: number;
      /** Why it last booted — separates a power fault from a network one. */
      resetReason?: string;
      /** The box could not reach its pinned antenna and joined by name instead. */
      pinFellBack?: boolean;
   };
   router?: {
      model?: 'hex-rb750gr3' | 'rb4011' | 'ccr2004' | 'ccr2116';
      platform?: 'mikrotik' | 'openwrt';
      hwBoard?: string;
      /** Lite firmware OTA: running version + the version an admin armed. */
      firmwareVersion?: string;
      /** No-reboot (bundle) update the router runs on top of its image.
       *  Already on the wire — listAdminVendoDevices sends the whole document. */
      bundleVersion?: string;
      targetFirmwareVersion?: string | null;
      vpnProvisionedAt?: string | null;
      lastRestProbeAt?: string | null;
      lastRestOkAt?: string | null;
      lastRestError?: string | null;
      /** When the router agent last spoke to us. Already returned by
       *  listAdminVendoDevices (it sends the whole document) — it was simply
       *  missing from this type, so the admin table could not show it. */
      lastSeenAt?: string | null;
      /** MikroTik Hybrid only: the cloud's record of what it wrote to the router (see api/vendo.ts). */
      hexSync?: {
         at?: string | null;
         dirtyAt?: string | null;
         liveAt?: string | null;
         error?: string | null;
      } | null;
      /** The addresses of THIS ROUTER'S OWN radios, reported by the agent.
       *  Absent on MikroTik and on older agents — and absence must read as
       *  "unknown", never as "the NodeMCU is somewhere wrong". */
      apBssids?: { bssid: string; freq: number }[];
      /** The box's own connection history. Same field the operator's Connection
       *  tab reads; needed here so the admin fleet list can answer "how long has
       *  this been down" without opening each device. */
      diag?: {
         hsAge?: number;
         inet?: string;
         reboots24h?: number;
         outage?: string;
         events?: string[];
         /** 🔴 0 MEANS "DID NOT SAY" — the server default for every router
          *  predating the field. Only a positive number is a reading. */
         freeKb?: number | null;
         /** What the router TRIED about a fault: `<epoch> action=<name>
          *  result=ok|fail`. On the router's flash, so these survive the
          *  reboot that ends the outage. */
         actions?: string[];
         at?: string | null;
      } | null;
      /** What the router says about its own condition — the CPU/RAM/Storage
       *  columns in the fleet list, and the device card's System Info face.
       *  Null on a router that reports none of it.
       *
       *  🔴 EVERY FIELD IS NULLABLE AND null MEANS "DID NOT SAY". Not zero. */
      sys?: {
         /** 1-minute load average × 100. NOT a percentage on its own: 0.47
          *  across 4 CPUs is about 12% busy. */
         loadCenti?: number | null;
         cores?: number | null;
         memTotalKb?: number | null;
         /** MemAvailable, never MemFree. */
         memAvailKb?: number | null;
         cfgFreeKb?: number | null;
         cfgTotalKb?: number | null;
         dlFreeKb?: number | null;
         dlTotalKb?: number | null;
         clients?: number | null;
         at?: string | null;
      } | null;
   };
   stats: {
      totalCoins: number;
      todayCoins: number;
      totalSessions: number;
      activeSessions: number;
      /** All-time revenue, untouched by Reset Stats. Survives forever. */
      lifetimeCoins: number;
      /** All-time session count, untouched by Reset Stats. */
      lifetimeSessions: number;
   };
   // Server-aggregated fields appended by listAdminVendoDevices.
   totalRevenue: number;
   gcashRevenue: number;
   activeSessionCount: number;
   /** Sessions PAUSED today (Philippine day), to pair with the live active count. */
   pausedSessionCount?: number;
   /** Mid-sale state — populated when a DeviceTransfer with
    *  status='pending' exists for this device. Drives the "Pending"
    *  badge in the admin fleet table. */
   pendingTransfer: {
      _id: string;
      device: string;
      toEmail: string;
      paymentType: 'free' | 'full-cash' | 'installment';
      fullCashAmount: number | null;
      initiatedAt: string;
      expiresAt: string;
   } | null;
   /** Set by the transferService on accept — null = never transferred. */
   lastTransferredAt: string | null;
   /** null = main vendo; ObjectId string = parent main's _id. */
   parent: string | null;
   /** 802.1Q VLAN id for sub-vendos (server-computed; null for mains). */
   vlanId?: number | null;
   createdAt: string;
   updatedAt: string;
}

export interface AdminVendoSession {
   _id: string;
   /** Server-synthesized whitelist row (id wl:<deviceId>:<MAC>) — always
    *  active, expiresAt mirrors the device license (null = Unlimited). */
   isWhitelist?: boolean;
   device: { mac: string; ip?: string; hostname?: string };
   payment: {
      method: 'coin' | 'gcash' | 'voucher';
      amount: number;
      voucherCode?: string | null;
   };
   time: {
      purchased: number;
      remaining: number;
      startedAt: string;
      expiresAt: string | null;
      pausedAt?: string | null;
      /** TOTAL time paused across every pause, in ms. */
      pausedTotalMs?: number;
   };
   bandwidth: { download: number; upload: number; plan?: string };
   status: 'active' | 'expired' | 'paused' | 'kicked';
   vendoDevice: { _id: string; name?: string; deviceId?: string } | string | null;
   owner: { _id: string; name?: string; email: string } | string;
   createdAt: string;
   updatedAt: string;
}

export interface SalesTimeseriesPoint {
   date: string; // YYYY-MM-DD
   credits: number;
   packageCredits: number;
   inquiryCredits: number;
}

export interface SalesTimeseriesResponse {
   days: number;
   series: SalesTimeseriesPoint[];
}

// Distributor applications (admin queue)
export const getAdminDistributorApplications = async (
   status?: ApplicationStatusServer | 'all'
): Promise<AdminDistributorApplication[]> => {
   const q = status && status !== 'all' ? `?status=${status}` : '';
   const r = await apiClient.get(`/admin/distributor-applications${q}`);
   return r.data.applications;
};

export const adminApproveDistributorApplication = async (id: string) => {
   const r = await apiClient.post(`/admin/distributor-applications/${id}/approve`);
   return r.data;
};

export const adminRejectDistributorApplication = async (
   id: string,
   rejectionReason: string
) => {
   const r = await apiClient.post(`/admin/distributor-applications/${id}/reject`, {
      rejectionReason,
   });
   return r.data;
};

export interface AdminDistributorPartner {
   _id: string;
   user: { _id: string; name?: string; email?: string } | string;
   displayName: string;
   businessName: string;
   region: string;
   city: string;
   photoUrl?: string;
   defaultPricePerCredit: number;
   tier: 'bronze' | 'silver' | 'gold' | 'diamond' | 'legendary';
   cumulativeCreditsSold: number;
   isActive: boolean;
   isHidden?: boolean;
   createdAt: string;
   updatedAt: string;
}

export const getAdminDistributorPartners = async (): Promise<AdminDistributorPartner[]> => {
   const r = await apiClient.get('/admin/distributor-partners');
   return r.data.partners;
};

export const adminUpdateDistributorPartner = async (
   id: string,
   patch: { isHidden: boolean }
): Promise<AdminDistributorPartner> => {
   const r = await apiClient.patch(`/admin/distributor-partners/${id}`, patch);
   return r.data.partner;
};

// Credit purchases (admin queue)
export const getAdminCreditPurchases = async (
   status?: PurchaseStatusServer | 'all'
): Promise<AdminCreditPurchase[]> => {
   const q = status && status !== 'all' ? `?status=${status}` : '';
   const r = await apiClient.get(`/admin/credit-purchases${q}`);
   return r.data.purchases;
};

export const adminApproveCreditPurchase = async (
   id: string,
   pricePerCredit?: number
) => {
   const r = await apiClient.post(`/admin/credit-purchases/${id}/approve`, {
      pricePerCredit,
   });
   return r.data;
};

export const adminRejectCreditPurchase = async (id: string, rejectionReason: string) => {
   const r = await apiClient.post(`/admin/credit-purchases/${id}/reject`, {
      rejectionReason,
   });
   return r.data;
};

// System settings (singleton: SmartFi receipt details)
export const getAdminSystemSettings = async (): Promise<SystemSettings> => {
   const r = await apiClient.get('/admin/system-settings');
   return r.data.settings;
};

export const updateAdminSystemSettings = async (
   patch: Partial<SystemSettings>
): Promise<SystemSettings> => {
   const r = await apiClient.put('/admin/system-settings', patch);
   return r.data.settings;
};

// ── Master voucher codes (platform-admin, redeemable on ANY vendo) ──
// Reusable until switched off, so `active` is a kill switch: a leaked code
// is free WiFi fleet-wide until it's flipped. redeemCount/lastRedeemedAt
// are surfaced so abuse is visible at a glance.

export interface AdminMasterVoucher {
   _id: string;
   code: string;
   label: string;
   minutes: number;
   active: boolean;
   redeemCount: number;
   lastRedeemedAt?: string | null;
   createdBy?: string;
   createdAt?: string;
}

export const listAdminMasterVouchers = async (): Promise<AdminMasterVoucher[]> => {
   const r = await apiClient.get('/admin/master-vouchers');
   return r.data.vouchers ?? [];
};

export const createAdminMasterVoucher = async (body: {
   code?: string;
   label?: string;
   minutes: number;
}): Promise<AdminMasterVoucher> => {
   const r = await apiClient.post('/admin/master-vouchers', body);
   return r.data.voucher;
};

export const updateAdminMasterVoucher = async (
   id: string,
   patch: { active?: boolean; label?: string; minutes?: number }
): Promise<AdminMasterVoucher> => {
   const r = await apiClient.patch(`/admin/master-vouchers/${id}`, patch);
   return r.data.voucher;
};

export const deleteAdminMasterVoucher = async (id: string) => {
   const r = await apiClient.delete(`/admin/master-vouchers/${id}`);
   return r.data;
};

// Inquiry reversal (dispute resolution)
export const adminReverseInquiry = async (id: string, note?: string) => {
   const r = await apiClient.post(`/admin/inquiries/${id}/reverse`, { note });
   return r.data;
};

// Admin-wide DeviceTransfer audit (cross-tenant)
export interface AdminDeviceTransfer {
   _id: string;
   device: { _id: string; name?: string; deviceId?: string } | string | null;
   fromOwner: { _id: string; name?: string; email: string } | string;
   toOwner: { _id: string; name?: string; email: string } | string | null;
   toEmail: string;
   status: 'pending' | 'accepted' | 'rejected' | 'cancelled' | 'expired';
   paymentType: 'free' | 'full-cash' | 'installment';
   fullCashAmount: number | null;
   fullCashPaidAt: string | null;
   note: string | null;
   claimCode: string | null;
   initiatedAt: string;
   expiresAt: string;
   acceptedAt: string | null;
   rejectedAt: string | null;
   cancelledAt: string | null;
}

export interface AdminTransfersQuery {
   status?: AdminDeviceTransfer['status'] | '';
   q?: string;
   limit?: number;
}

export const getAdminTransfers = async (
   query: AdminTransfersQuery = {}
): Promise<AdminDeviceTransfer[]> => {
   const r = await apiClient.get<{ transfers: AdminDeviceTransfer[] }>(
      '/admin/vendo/transfers',
      { params: query }
   );
   return r.data.transfers;
};

// Admin-wide vendo fleet (cross-tenant device list)
export const getAdminVendoDevices = async (): Promise<AdminVendoDevice[]> => {
   const r = await apiClient.get('/admin/vendo/devices');
   return r.data.devices;
};

/**
 * ONE router, the whole document. The fleet list is a projection of the
 * columns it draws (5.6 MB → well under 1 MB for 840 routers, 2026-09-28);
 * the Settings modal edits rates, portal layout, WAN and NodeMCU WiFi, so it
 * asks for its device here instead of reading the list.
 */
export const getAdminVendoDevice = async (id: string): Promise<AdminVendoDevice> => {
   const r = await apiClient.get(`/admin/vendo/devices/${id}`);
   return r.data.device;
};

// Router-config OTA rollout — per-device version state for the fleet
// table column. Pairs with rolloutVendoConfig() to push updates.
export interface RolloutStatusRow {
   _id: string;
   name: string;
   deviceId: string;
   owner: { email: string; name: string } | null;
   status: 'online' | 'offline' | 'error';
   appliedVersion: number;
   lastAppliedAt: string | null;
   lastAppliedOk: boolean;
   lastAppliedError: string | null;
   stale: boolean;
}

export interface RolloutStatusResponse {
   currentVersion: number;
   devices: RolloutStatusRow[];
}

export const getVendoRolloutStatus = async (): Promise<RolloutStatusResponse> => {
   const r = await apiClient.get('/admin/vendo/rollout-status');
   return r.data;
};

export interface RolloutResultRow {
   _id: string;
   name: string;
   ok: boolean;
   error?: string;
}

export interface RolloutResponse {
   currentVersion: number;
   attempted: number;
   succeeded: number;
   failed: number;
   results: RolloutResultRow[];
}

export const rolloutVendoConfig = async (
   deviceId?: string
): Promise<RolloutResponse> => {
   const r = await apiClient.post('/admin/vendo/rollout-config', {
      deviceId,
   });
   return r.data;
};

// Arm a firmware OTA for a Lite device → the box flashes the active Ruijie image
// on its next heartbeat.
export const armFirmwareOta = async (
   deviceId: string
): Promise<{ ok: boolean; target: string }> => {
   const r = await apiClient.post('/admin/vendo/firmware-ota', { deviceId });
   return r.data;
};

// Arm the NodeMCU firmware OTA — flips nodeMcu.otaOptIn so the box
// self-updates to the active prod NodeMCU image on its next heartbeat.
export const armNodeMcuOta = async (
   deviceId: string
): Promise<{ ok: boolean; otaOptIn: boolean }> => {
   const r = await apiClient.post('/admin/vendo/nodemcu-ota', { deviceId });
   return r.data;
};

// Fleet rollout summary for the 3 rollout cards (MikroTik config push, Ruijie
// image OTA, NodeMCU OTA) — each its own version + how many are behind.
export interface RolloutSummaryResponse {
   mikrotik: { currentVersion: number; total: number; staleCount: number };
   ruijie: { activeVersion: string | null; total: number; outdatedCount: number };
   nodemcu: { activeVersion: string | null; total: number; outdatedCount: number };
}
export const getVendoRolloutSummary = async (): Promise<RolloutSummaryResponse> => {
   const r = await apiClient.get('/admin/vendo/rollout-summary');
   return r.data;
};

// Fleet-arm the active Ruijie image on every outdated Lite main (pull-OTA).
export const rolloutRuijieFirmware = async (): Promise<{
   ok: boolean;
   armed: number;
   target: string;
}> => {
   const r = await apiClient.post('/admin/vendo/rollout-ruijie', {});
   return r.data;
};

// Fleet-arm the NodeMCU OTA on every device whose NodeMCU is behind active.
export const rolloutNodeMcuFirmware = async (): Promise<{
   ok: boolean;
   armed: number;
   target: string;
}> => {
   const r = await apiClient.post('/admin/vendo/rollout-nodemcu', {});
   return r.data;
};

// Daily credit-flow timeseries for the admin Overview chart
export const getAdminVendoSalesTimeseries = async (
   days = 14
): Promise<SalesTimeseriesResponse> => {
   const r = await apiClient.get(`/admin/vendo/sales-timeseries?days=${days}`);
   return r.data;
};

// Daily platform-wide revenue (voucher + coin) for the admin Overview chart.
// Optional from/to (YYYY-MM-DD); defaults server-side to the last 30 days.
export interface RevenueTimeseriesResponse {
   from: string;
   to: string;
   series: { date: string; voucherRevenue: number; coinRevenue: number; onlineRevenue?: number }[];
}
export const getAdminVendoRevenueTimeseries = async (
   from?: string,
   to?: string
): Promise<RevenueTimeseriesResponse> => {
   const params = new URLSearchParams();
   if (from) params.set('from', from);
   if (to) params.set('to', to);
   const qs = params.toString();
   const r = await apiClient.get(
      `/admin/vendo/revenue-timeseries${qs ? `?${qs}` : ''}`
   );
   return r.data;
};

/**
 * The two non-money series behind the chart's metric switch.
 *
 * ⚠️ `peakOnlineVendos` is `number | null` and the null is load-bearing:
 * nothing ever recorded fleet availability over time, so every day before the
 * sampler cron shipped genuinely has no answer. It MUST render as a gap in the
 * line — a 0 would read as "the whole fleet was dark that day".
 */
/**
 * When a router changed how it reaches us, newest first.
 *
 * ⚠️ `ip` is the peer our nginx actually saw — PROVEN. `claimedIp` is the
 * shop's own address as asserted by the forwarding chain on a relayed
 * check-in — client-supplied and UNPROVEN. Never merge them for display.
 *
 * ⚠️ On a relayed row `ip` is Vercel's OUTBOUND address, which is Amazon
 * Singapore (13.x / 54.179.x). An AWS address here is CORRECT.
 */
export interface DeviceRoadEventRow {
   at: string;
   via: 'direct' | 'relay';
   road: number;
   ip: string;
   claimedIp: string;
}
export const getAdminDeviceRoadHistory = async (
   id: string
): Promise<{ events: DeviceRoadEventRow[] }> => {
   const r = await apiClient.get(`/admin/vendo/devices/${id}/road-history`);
   return r.data;
};

export interface MetricsTimeseriesResponse {
   from: string;
   to: string;
   series: {
      date: string;
      peakSessions: number;
      peakOnlineVendos: number | null;
   }[];
}
export const getAdminVendoMetricsTimeseries = async (
   from?: string,
   to?: string
): Promise<MetricsTimeseriesResponse> => {
   const params = new URLSearchParams();
   if (from) params.set('from', from);
   if (to) params.set('to', to);
   const qs = params.toString();
   const r = await apiClient.get(
      `/admin/vendo/metrics-timeseries${qs ? `?${qs}` : ''}`
   );
   return r.data;
};

export interface AdminVendoSessionsPage {
   sessions: AdminVendoSession[];
   total: number;
   /** Keyed by session._id — the pause window in force, in minutes.
    *  null = frozen time is kept for ever. */
   pauseWindowBySessionId?: Record<string, number | null>;
   /** Keyed by session._id, only for a row holding MORE than one entry: what
    *  went into it (coin, voucher, online, operator), in order. */
   entriesBySessionId?: Record<string, SessionEntry[]>;
   /** The server's own clock, so the countdown does not trust the viewing
    *  device's — see utils/serverClock. */
   serverNow?: string;
   page: number;
   limit: number;
}

// All currently-active sessions across every owner, paginated.
export const getAdminVendoActiveSessions = async (
   page = 1,
   limit = 20,
   opts: { method?: string; ownerEmail?: string; status?: string; q?: string } = {}
): Promise<AdminVendoSessionsPage> => {
   const r = await apiClient.get('/admin/vendo/sessions', {
      params: {
         page,
         limit,
         ...(opts.method ? { method: opts.method } : {}),
         ...(opts.ownerEmail ? { ownerEmail: opts.ownerEmail } : {}),
         ...(opts.status ? { status: opts.status } : {}),
         ...(opts.q ? { q: opts.q } : {}),
      },
   });
   return r.data;
};

export interface AdminVendoStatsResponse {
   transactionsLifetime: number;
   transactionsToday: number;
   /** Filled only when from/to provided. */
   transactionsPeriod: number | null;
   creditsUsed: number;
   /** Filled only when from/to provided. */
   creditsUsedPeriod: number | null;
}

export const getAdminVendoStats = async (
   opts: { from?: string; to?: string } = {}
): Promise<AdminVendoStatsResponse> => {
   const params: Record<string, string> = {};
   if (opts.from) params.from = opts.from;
   if (opts.to) params.to = opts.to;
   const r = await apiClient.get('/admin/vendo/stats', { params });
   return r.data;
};

// ─── Voucher view (admin-panel-wifi · "Switch to Vouchers" toggle) ───

export interface AdminVoucherTransactionRow {
   _id: string;
   vendoDevice: { _id: string; name: string; deviceId: string } | null;
   owner: { _id: string; email: string } | null;
   customer: string;
   mac: string;
   ip: string;
   amount: number;
   minutes: number;
   paymentMethod: 'gcash';
   status: 'pending' | 'success' | 'failed';
   voucherCode?: string;
   failureReason?: string;
   platformFee: number;
   xenditFee: number;
   netAmount: number;
   vat: number;
   createdAt: string;
   updatedAt: string;
}

export interface AdminVoucherTransactionsResponse {
   total: number;
   page: number;
   limit: number;
   rows: AdminVoucherTransactionRow[];
}

export const getAdminVoucherTransactions = async (opts: {
   page?: number;
   limit?: number;
   status?: 'pending' | 'success' | 'failed';
   deviceId?: string;
   ownerId?: string;
   from?: string;
   to?: string;
} = {}): Promise<AdminVoucherTransactionsResponse> => {
   const params: Record<string, string | number> = {};
   if (opts.page) params.page = opts.page;
   if (opts.limit) params.limit = opts.limit;
   if (opts.status) params.status = opts.status;
   if (opts.deviceId) params.deviceId = opts.deviceId;
   if (opts.ownerId) params.ownerId = opts.ownerId;
   if (opts.from) params.from = opts.from;
   if (opts.to) params.to = opts.to;
   const r = await apiClient.get('/admin/vendo/voucher/transactions', { params });
   return r.data;
};

export interface AdminVoucherStatsResponse {
   totalSales: number;
   commissionSales: number;
   customerPayable: number;
   pendingPayout: number;
   settledPayouts: number;
   totalDisbursementFees: number;
   totalUsers: number;
   totalDevices: number;
   totalTransactions: number;
   vouchersSoldGcash: number;
   vouchersSoldManual: number;
   vouchersSoldTotal: number;
   vouchersUnredeemed: number;
   successfulTransactions: number;
   withdrawable: number;
}

export interface AdminVoucherSalesChartResponse {
   items: Array<{
      date: string;
      label: string;
      revenue: number;
      commission: number;
   }>;
}

export const getAdminVoucherSalesChart = async (opts: {
   from?: string;
   to?: string;
} = {}): Promise<AdminVoucherSalesChartResponse> => {
   const r = await apiClient.get('/admin/vendo/voucher/sales-chart', {
      params: opts,
   });
   return r.data;
};

export interface AdminVoucherPayoutRow {
   _id: string;
   owner: { _id: string; email: string } | null;
   amount: number;
   disbursementFee: number;
   netAmount: number;
   payoutMethod: string;
   payoutAccount: string;
   payoutAccountNumber: number;
   status: 'processing' | 'settled' | 'failed' | 'cancelled' | 'rejected';
   createdAt: string;
   updatedAt: string;
}

export const listAdminVoucherPayouts = async (opts: {
   page?: number;
   limit?: number;
   status?: string;
} = {}): Promise<{
   total: number;
   page: number;
   limit: number;
   rows: AdminVoucherPayoutRow[];
}> => {
   const r = await apiClient.get('/admin/vendo/voucher/payouts', {
      params: opts,
   });
   return r.data;
};

export const settleAdminVoucherPayout = async (id: string) => {
   const r = await apiClient.put(`/admin/vendo/voucher/payouts/${id}/settle`);
   return r.data;
};

export const rejectAdminVoucherPayout = async (id: string) => {
   const r = await apiClient.put(`/admin/vendo/voucher/payouts/${id}/reject`);
   return r.data;
};

export interface AdminVoucherUserRow {
   _id: string;
   email: string;
   name: string;
   verified: boolean;
   createdAt: string | null;
   devicesCount: number;
   totalTransactions: number;
   totalRevenue: number;
}

export const listAdminVoucherUsers = async (opts: {
   page?: number;
   limit?: number;
} = {}): Promise<{
   total: number;
   page: number;
   limit: number;
   rows: AdminVoucherUserRow[];
}> => {
   const r = await apiClient.get('/admin/vendo/voucher/users', {
      params: opts,
   });
   return r.data;
};

export interface AdminVoucherDeviceRow {
   _id: string;
   name: string;
   deviceId: string;
   portalSlug: string;
   portalTheme: string;
   owner: { _id: string; email: string } | null;
   sales: number;
   payable: number;
   transactionsCount: number;
   createdAt: string | null;
}

export const listAdminVoucherDevices = async (opts: {
   page?: number;
   limit?: number;
} = {}): Promise<{
   total: number;
   page: number;
   limit: number;
   rows: AdminVoucherDeviceRow[];
}> => {
   const r = await apiClient.get('/admin/vendo/voucher/devices', {
      params: opts,
   });
   return r.data;
};

export const getAdminVoucherStats = async (opts: {
   from?: string;
   to?: string;
} = {}): Promise<AdminVoucherStatsResponse> => {
   const params: Record<string, string> = {};
   if (opts.from) params.from = opts.from;
   if (opts.to) params.to = opts.to;
   const r = await apiClient.get('/admin/vendo/voucher/stats', { params });
   return r.data;
};

/* ─── Firmware multi-version registry ─────────────────────────── */

export type FirmwareChannel = 'dev' | 'prod';
export type FirmwareTarget =
   | 'nodemcu'
   // The NodeMCU of a MikroTik Hybrid vendo. Its own image line: it shares
   // 'hybrid' with the Ruijie/OrangePi NodeMCUes but not the firmware.
   | 'nodemcu-mikrotik'
   | 'ruijie'
   | 'orangepi'
   | 'orangepi-pc'
   | 'orangepi-zero3'
   | 'comfast-ew71'
   | 'comfast-ew72'
   | 'rt2980'
   | 'rt2983'
   | 'newifi-d2'
   | '360-t6m'
   | 'x86-64';

export interface AdminFirmwareVersion {
   /** Devices running this exact version, and devices sent it but not yet on it.
    *  Lets the firmware list show which images have actually been canaried. */
   devicesOnIt?: number;
   devicesArmed?: number;
   /** Product type — cloud or hybrid. Legacy rows have none and ARE cloud. */
   variant?: 'cloud' | 'hybrid';
   _id: string;
   version: string;
   channel: FirmwareChannel;
   /** Hardware this image is for. Legacy rows may omit it (treat as nodemcu). */
   target?: FirmwareTarget;
   filename: string;
   r2Key: string;
   size: number;
   sha256: string | null;
   uploadedBy: { _id: string; email: string; name?: string } | string;
   uploadedAt: string;
   isActive: boolean;
   notes: string;
   createdAt: string;
   updatedAt: string;
}

export interface FirmwareUploadUrlResponse {
   uploadUrl: string;
   key: string;
   filename: string;
   expiresIn: number;
}

export const getAdminFirmwareUploadUrl = async (input: {
   version: string;
   channel: FirmwareChannel;
   target: FirmwareTarget;
   size: number;
   contentType?: string;
   /**
    * The ORIGINAL file name. The server reads the product type (cloud/hybrid)
    * straight out of it, so the operator never has to restate what the build
    * already wrote into the name. Without this the server has nothing to read
    * and every upload silently registers as cloud — which is exactly what
    * happened on 2026-08-02: a hybrid image landed as
    * smartfi-orangepi-prod-1.0.28.img.gz with no hybrid marker at all.
    */
   filename: string;
}): Promise<FirmwareUploadUrlResponse> => {
   const r = await apiClient.post('/admin/firmware/upload-url', input);
   return r.data;
};

export const registerAdminFirmware = async (input: {
   version: string;
   channel: FirmwareChannel;
   target: FirmwareTarget;
   key: string;
   size: number;
   sha256?: string;
   notes?: string;
}): Promise<AdminFirmwareVersion> => {
   const r = await apiClient.post('/admin/firmware', input);
   return r.data.firmware;
};

export const getAdminFirmwares = async (
   channel?: FirmwareChannel,
   target: FirmwareTarget = 'nodemcu'
): Promise<AdminFirmwareVersion[]> => {
   const params = new URLSearchParams({ target });
   if (channel) params.set('channel', channel);
   const r = await apiClient.get(`/admin/firmware?${params.toString()}`);
   return r.data.firmwares;
};

export const activateAdminFirmware = async (
   id: string
): Promise<AdminFirmwareVersion> => {
   const r = await apiClient.post(`/admin/firmware/${id}/activate`);
   return r.data.firmware;
};

/**
 * Canary: send ONE version to CHOSEN devices, separate from activate.
 *
 * Activate declares the fleet default and must keep meaning only that, or the
 * firmware list can no longer answer "what is the current release?". Before this
 * existed, putting an image on three boxes meant editing the production database
 * by hand.
 */
export interface FirmwareCandidate {
   id: string;
   name: string;
   deviceId: string;
   ownerEmail: string;
   ownerName: string;
   /** What it is running NOW. Choosing from names alone is how a hybrid image
    *  reaches a cloud box — a one-way migration with no coin path to land on. */
   runningVersion: string;
   variant: 'cloud' | 'hybrid';
   platform: string;
   lastSeenAt: string | null;
   eligible: boolean;
   skipReason: string | null;
   /** The refusal in words. skipReason stays the CODE, which the list filters on. */
   skipText?: string | null;
   /** Already flashed to this version. */
   onThisVersion: boolean;
   /** Sent, but the box has not taken it yet. */
   armedForThis: boolean;
}

export const getFirmwareCandidates = async (
   id: string
): Promise<{
   version: string;
   variant: 'cloud' | 'hybrid';
   devices: FirmwareCandidate[];
}> => {
   const r = await apiClient.get(`/admin/firmware/${id}/candidates`);
   return r.data;
};

export const sendFirmwareToDevices = async (
   id: string,
   deviceIds: string[]
): Promise<{
   ok: boolean;
   armed: number;
   summary: string;
   skipped: Array<{ deviceId: string; name: string; reason: string; why: string }>;
}> => {
   const r = await apiClient.post(`/admin/firmware/${id}/send`, { deviceIds });
   return r.data;
};

/** One NodeMCU, as a candidate for a specific image. */
export interface CoinBoxCandidate {
   id: string;
   name: string;
   deviceId: string;
   ownerEmail: string;
   /** Coin-box firmware it reports now; '' when it has never reported one. */
   runningVersion: string;
   variant: 'cloud' | 'hybrid';
   hasCoinBox: boolean;
   /** The NodeMCU is reached THROUGH the router, so this decides reachability. */
   routerStatus: string;
   isSub: boolean;
   /** A version already sent to this board and not yet taken. */
   sentVersion: string | null;
   optedIn: boolean;
   eligible: boolean;
   skipReason: string | null;
   /** Why it was refused, in words an operator can act on. */
   skipText: string | null;
}

/**
 * Coin-box candidates, ADAPTED to the same shape the router picker uses.
 *
 * The two endpoints answer different questions and carry different fields, but
 * the screen asking them is the same screen. Adapting here rather than forking
 * the component keeps one picker: a second 355-line copy would drift on the
 * first change, and the fields that matter most are the ones that stop a bad
 * send.
 */
export const getCoinBoxCandidates = async (
   id: string
): Promise<{
   version: string;
   variant: 'cloud' | 'hybrid';
   devices: FirmwareCandidate[];
}> => {
   const r = await apiClient.get(`/admin/firmware/${id}/coinbox-candidates`);
   const base = (v: string) => (v || '').split('-')[0];
   return {
      version: r.data.version,
      variant: r.data.variant,
      devices: (r.data.devices as CoinBoxCandidate[]).map((d) => ({
         id: d.id,
         name: d.name,
         deviceId: d.deviceId,
         ownerEmail: d.ownerEmail,
         ownerName: '',
         runningVersion: d.runningVersion,
         variant: d.variant,
         // The NodeMCU has no platform of its own — it is reached through the
         // router, so its reachability IS the router's.
         platform: d.routerStatus,
         lastSeenAt: null,
         eligible: d.eligible,
         // ⚠️ The refusal CODE, not the sentence. The picker filters
         // wrong-variant boxes out of the list entirely by comparing this to
         // 'wrong-variant' — mapping the sentence in here broke that silently,
         // and every cloud box showed up in a hybrid send with a greyed-out
         // row. The sentence rides along in skipText for the hover.
         skipReason: d.skipReason,
         skipText: d.skipText,
         onThisVersion: base(d.runningVersion) === base(r.data.version),
         armedForThis: d.sentVersion === r.data.version,
      })),
   };
};

export const sendCoinBoxToDevices = async (
   id: string,
   deviceIds: string[]
): Promise<{
   ok: boolean;
   armed: number;
   summary: string;
   skipped: Array<{ deviceId: string; name: string; reason: string; why: string }>;
}> => {
   const r = await apiClient.post(`/admin/firmware/${id}/send-coinbox`, {
      deviceIds,
   });
   // Same adaptation as the candidates above: one picker, one shape. `why` is
   // the sentence an operator reads; `reason` is the code behind it.
   return {
      ...r.data,
      skipped: (r.data.skipped as Array<{
         name: string;
         reason: string;
         text: string;
      }>).map((s) => ({
         deviceId: s.name,
         name: s.name,
         reason: s.reason,
         why: s.text,
      })),
   };
};

export const deleteAdminFirmware = async (id: string): Promise<void> => {
   await apiClient.delete(`/admin/firmware/${id}`);
};

// ─── Downloads catalog (externally-hosted reference files) ───────────────
// Separate from the firmware registry: these files are only HOSTED for
// download (the "remove SmartFi → OpenWRT" image, the Ruijie 1.317 downgrade)
// and never enter the OTA path. Public download is /api/openwrt/external/:slug/download.
export type ExternalDownloadKind = 'router-image' | 'pc-download';

export interface AdminExternalDownload {
   _id: string;
   slug: string;
   label: string;
   kind: ExternalDownloadKind;
   board: string;
   filename: string;
   r2Key: string;
   size: number;
   sha256: string | null;
   contentType: string;
   uploadedBy: { _id: string; email: string; name?: string } | string;
   uploadedAt: string;
   notes: string;
   createdAt: string;
   updatedAt: string;
}

export const getAdminExternalUploadUrl = async (input: {
   slug: string;
   filename: string;
   size: number;
   contentType?: string;
}): Promise<{ uploadUrl: string; key: string; expiresIn: number }> => {
   const r = await apiClient.post('/admin/external/upload-url', input);
   return r.data;
};

export const registerAdminExternal = async (input: {
   slug: string;
   label: string;
   kind: ExternalDownloadKind;
   board?: string;
   filename: string;
   key: string;
   size: number;
   contentType?: string;
   notes?: string;
}): Promise<AdminExternalDownload> => {
   const r = await apiClient.post('/admin/external', input);
   return r.data.external;
};

export const getAdminExternals = async (): Promise<AdminExternalDownload[]> => {
   const r = await apiClient.get('/admin/external');
   return r.data.externals;
};

export const deleteAdminExternal = async (id: string): Promise<void> => {
   await apiClient.delete(`/admin/external/${id}`);
};

// ─── Incentives catalog admin CRUD (points system) ──────────────────────
import type { ApiIncentive } from './vendo';

export async function listAdminIncentivesApi(): Promise<{ incentives: ApiIncentive[] }> {
   const res = await apiClient.get('/admin/incentives');
   return res.data;
}
export async function createAdminIncentiveApi(
   body: Partial<ApiIncentive>
): Promise<{ incentive: ApiIncentive }> {
   const res = await apiClient.post('/admin/incentives', body);
   return res.data;
}
export async function updateAdminIncentiveApi(
   id: string,
   body: Partial<ApiIncentive>
): Promise<{ incentive: ApiIncentive }> {
   const res = await apiClient.put(`/admin/incentives/${id}`, body);
   return res.data;
}
export async function deleteAdminIncentiveApi(id: string): Promise<{ ok: boolean }> {
   const res = await apiClient.delete(`/admin/incentives/${id}`);
   return res.data;
}

// ─── Redemption queue + points monitoring (admin-panel-points) ───────────
import type { ApiRedemption } from './vendo';

export interface AdminRedemption extends Omit<ApiRedemption, 'user'> {
   user: { _id: string; name?: string; email: string } | string;
}

export async function listAdminRedemptionsApi(
   status?: 'pending' | 'fulfilled' | 'rejected'
): Promise<{ redemptions: AdminRedemption[] }> {
   const res = await apiClient.get('/admin/redemptions', {
      params: status ? { status } : undefined,
   });
   return res.data;
}

export async function decideAdminRedemptionApi(
   id: string,
   action: 'fulfill' | 'reject',
   adminNote?: string
): Promise<{ redemption: AdminRedemption; refundedBalance?: number }> {
   const res = await apiClient.put(`/admin/redemptions/${id}`, { action, adminNote });
   return res.data;
}

export interface AdminPointsUserRow {
   userId: string;
   name?: string;
   email?: string;
   role: 'distributor' | 'reseller' | 'user';
   balance: number;
   earned: number;
   redeemed: number;
   ledgerSum: number;
   awardedByReason: Record<string, number>;
   mismatch: boolean;
}

export interface AdminPointsOverview {
   summary: {
      walletsOutstanding: number;
      totalAwarded: number;
      totalRedeemed: number;
      redemptionCount: number;
      pendingRedemptions: number;
      mismatchCount: number;
   };
   users: AdminPointsUserRow[];
}

export async function getAdminPointsOverviewApi(): Promise<AdminPointsOverview> {
   const res = await apiClient.get('/admin/points/overview');
   return res.data;
}

export interface AdminPointsLedgerRow {
   _id: string;
   owner: { _id: string; name?: string; email: string } | string;
   delta: number;
   reason: string;
   balanceAfter: number;
   refType?: string;
   refId?: string;
   note?: string;
   createdAt: string;
}

export async function getAdminPointsLedgerApi(params?: {
   reason?: string;
   userId?: string;
   limit?: number;
}): Promise<{ ledger: AdminPointsLedgerRow[] }> {
   const res = await apiClient.get('/admin/points/ledger', { params });
   return res.data;
}

// ─── Admin credit-flow monitoring (admin-panel-points → Credits tab) ────

export interface AdminCreditOverview {
   summary: {
      issuedToDistributors: number;
      soldToBuyers: number;
      transferred: number;
      convertedFromPoints: number;
      consumed: number;
      transferCount: number;
      // Admin credit movement. Kept out of every figure above and split by
      // what it was for.
      adminGivenAway: number;
      adminSelfActivation: number;
      adminClawedBack: number;
      /** How much of the giving came out of an admin's own balance. */
      adminSpentOwnStock: number;
   };
}

export async function getAdminCreditOverviewApi(): Promise<AdminCreditOverview> {
   const res = await apiClient.get('/admin/credits/overview');
   return res.data;
}

export interface AdminCreditLedgerRow {
   _id: string;
   owner: { _id: string; name?: string; email: string } | string;
   delta: number;
   reason: string;
   refType?: string;
   refId?: string;
   note?: string;
   /** Admin who caused the move — set on manual adjustments only. */
   actor?: { _id: string; name?: string; email: string } | string;
   /** The vendo, on activation / renewal rows (server looks it up by refId). */
   device?: { _id: string; name: string; deviceId?: string } | null;
   createdAt: string;
}

export async function getAdminCreditLedgerApi(params?: {
   reason?: string;
   userId?: string;
   limit?: number;
}): Promise<{ ledger: AdminCreditLedgerRow[] }> {
   const res = await apiClient.get('/admin/credits/ledger', { params });
   return res.data;
}

/**
 * Give credits to an account, or take them back with a negative amount.
 * Admin-only; the acting admin needs no balance of their own.
 */
export async function adminAdjustCreditsApi(input: {
   userId?: string;
   email?: string;
   amount: number;
   note?: string;
}): Promise<{
   success: true;
   user: { id: string; name?: string; email: string };
   amount: number;
   /** The recipient's balance after the move. */
   balance: number;
   /** How much of a give came out of the admin's own balance. */
   spentFromBalance: number;
   /** The admin's own balance after the move. */
   actorBalance: number;
}> {
   const res = await apiClient.post('/admin/credits/adjust', input);
   return res.data;
}

// ─── Tenants directory + staff management ──────────────────────────────────

export type TenantSort =
   | 'newest'
   | 'oldest'
   | 'sales_desc'
   | 'credits_desc'
   | 'referrals_desc'
   | 'points_desc'
   | 'devices_desc'
   | 'credit_used_desc'
   | 'transfer_sales_desc';

export type TenantOnlineFilter = 'all' | 'online' | 'offline';

export interface AdminTenant {
   id: string;
   email: string;
   registeredAt: string;
   devices: number;
   online: boolean;
   todayCoin: number;
   lifetimeCoin: number;
   lifetimeGcash: number;
   lifetimeRevenue: number;
   credits: number;
   creditUsed: number;
   transferCount: number;
   transferSales: number;
   referrals: number;
   points: number;
   role: 'user' | 'staff' | 'admin';
   staffPermissions: string[];
   staffSince: string | null;
}

export interface AdminTenantsResponse {
   tenants: AdminTenant[];
   total: number;
   page: number;
   limit: number;
   sort: TenantSort;
   search: string;
}

export const getAdminTenants = async (params: {
   search?: string;
   sort?: TenantSort;
   online?: TenantOnlineFilter;
   page?: number;
   limit?: number;
}): Promise<AdminTenantsResponse> => {
   const sp = new URLSearchParams();
   if (params.search) sp.set('search', params.search);
   if (params.sort) sp.set('sort', params.sort);
   if (params.online && params.online !== 'all') sp.set('online', params.online);
   if (params.page) sp.set('page', String(params.page));
   if (params.limit) sp.set('limit', String(params.limit));
   const { data } = await apiClient.get(`/admin/tenants?${sp.toString()}`);
   return data;
};

/** Set a user's full set of granted staff tabs. Empty array = remove from staff. */
export const setTenantStaffPermissions = async (
   userId: string,
   permissions: string[]
): Promise<{
   id: string;
   email: string;
   role: 'user' | 'staff' | 'admin';
   staffPermissions: string[];
   staffSince: string | null;
}> => {
   const { data } = await apiClient.patch(`/admin/staff/${userId}`, { permissions });
   return data;
};

// ── Fleet-wide live coin ticker (admin panel) ──────────────────────────────
export interface AdminCoinRow {
   id: string;
   amount: number;
   minutes: number;
   mac: string | null;
   kind: 'new' | 'extend' | null;
   deviceId: string;
   deviceName: string;
   operator: string;
   type: 'main' | 'sub';
   vlanId: number | null;
   isp: string | null;
   at: string;
}
export interface AdminCoinsLiveResponse {
   coinsToday: number;
   coinCountToday: number;
   recentCoins: AdminCoinRow[];
}

export const getAdminCoinsLive = async (): Promise<AdminCoinsLiveResponse> => {
   const r = await apiClient.get('/admin/vendo/coins/live');
   return r.data;
};

export interface AdminCoinsListResponse {
   page: number;
   limit: number;
   total: number;
   coins: AdminCoinRow[];
}
export const getAdminCoins = async (
   q: string,
   page: number
): Promise<AdminCoinsListResponse> => {
   const params: Record<string, string> = { page: String(page) };
   if (q) params.q = q;
   const r = await apiClient.get('/admin/vendo/coins', { params });
   return r.data;
};

// ─── Posts: Knowledgebase, News and Blog (admin-panel-posts) ────────────
// One set of endpoints behind all three tabs; `type` separates them.

export type PostType = 'kb' | 'news' | 'blog';
export type NewsKind = 'feature' | 'improve' | 'fix';

export interface AdminPostSeo {
   metaTitle?: string;
   metaDescription?: string;
   ogImageUrl?: string;
}

/** A row in the list — the heavy body fields are left out on purpose. */
export interface AdminPostRow {
   _id: string;
   type: PostType;
   title: string;
   slug: string;
   slugHistory: string[];
   excerpt: string;
   coverUrl: string;
   category: string;
   tags: string[];
   published: boolean;
   publishedAt: string | null;
   order: number;
   seo: AdminPostSeo;
   kind?: NewsKind;
   version?: string;
   commentsEnabled: boolean;
   readMinutes: number;
   views: number;
   helpfulYes: number;
   helpfulNo: number;
   createdAt: string;
   updatedAt: string;
}

/** The full post, including the editor's own blocks. */
export interface AdminPost extends AdminPostRow {
   blocks: unknown[];
   html: string;
   plain: string;
}

export async function listAdminPostsApi(params: {
   type?: PostType;
   category?: string;
   q?: string;
}): Promise<{ posts: AdminPostRow[] }> {
   const res = await apiClient.get('/admin/posts', { params });
   return res.data;
}

export async function getAdminPostApi(id: string): Promise<{ post: AdminPost }> {
   const res = await apiClient.get(`/admin/posts/${id}`);
   return res.data;
}

export async function createAdminPostApi(
   body: Partial<AdminPost>
): Promise<{ post: AdminPost }> {
   const res = await apiClient.post('/admin/posts', body);
   return res.data;
}

export async function updateAdminPostApi(
   id: string,
   body: Partial<AdminPost>
): Promise<{ post: AdminPost }> {
   const res = await apiClient.put(`/admin/posts/${id}`, body);
   return res.data;
}

export async function deleteAdminPostApi(id: string): Promise<{ ok: boolean }> {
   const res = await apiClient.delete(`/admin/posts/${id}`);
   return res.data;
}

export async function reorderAdminPostsApi(
   ids: string[]
): Promise<{ ok: boolean; count: number }> {
   const res = await apiClient.post('/admin/posts/reorder', { ids });
   return res.data;
}

// ─── Post picture library ───────────────────────────────────────────────
// Its own collection, kept apart from the portal media library so a post
// picture never turns up in an operator's background gallery.

export interface PostMediaItem {
   _id: string;
   url: string;
   name: string;
   size: number;
   contentType: string;
   createdAt: string;
}

export async function listPostMediaApi(q?: string): Promise<{ media: PostMediaItem[] }> {
   const res = await apiClient.get('/admin/post-media', { params: q ? { q } : {} });
   return res.data;
}

export async function registerPostMediaApi(body: {
   url: string;
   name?: string;
   size?: number;
   contentType?: string;
}): Promise<{ media: PostMediaItem }> {
   const res = await apiClient.post('/admin/post-media', body);
   return res.data;
}

export async function deletePostMediaApi(id: string): Promise<{ ok: boolean }> {
   const res = await apiClient.delete(`/admin/post-media/${id}`);
   return res.data;
}

// ─── Setup guides (Admin Panel → Posts → Setup guides) ──────────────────

export interface SetupStep {
   title: string;
   /** The step body, written in the block editor. */
   blocks?: unknown[];
   /** Rendered by the server; read-only here. */
   html?: string;
   /** The older shape, still rendered when a step has no blocks yet. */
   lead: string;
   bullets: string[];
   ctaLabel?: string;
   ctaHref?: string;
}

export interface SetupRequirement {
   icon: string;
   name: string;
   detail: string;
   note: string;
}

export interface AdminSetupGuide {
   _id: string;
   device: string;
   name: string;
   tagline: string;
   note: string;
   order: number;
   published: boolean;
   hardware: SetupRequirement[];
   software: SetupRequirement[];
   steps: SetupStep[];
   updatedAt: string;
}

export async function listAdminSetupGuidesApi(): Promise<{ guides: AdminSetupGuide[] }> {
   const res = await apiClient.get('/admin/setup-guides');
   return res.data;
}

export async function createAdminSetupGuideApi(
   body: Partial<AdminSetupGuide> & { device: string }
): Promise<{ guide: AdminSetupGuide }> {
   const res = await apiClient.post('/admin/setup-guides', body);
   return res.data;
}

export async function updateAdminSetupGuideApi(
   id: string,
   body: Partial<AdminSetupGuide>
): Promise<{ guide: AdminSetupGuide }> {
   const res = await apiClient.put(`/admin/setup-guides/${id}`, body);
   return res.data;
}

export async function deleteAdminSetupGuideApi(id: string): Promise<{ ok: boolean }> {
   const res = await apiClient.delete(`/admin/setup-guides/${id}`);
   return res.data;
}

// ─── Support team (Piso WiFi settings → Support team) ───────────────────
// Shown in the help centre sidebar and behind Support in the profile menu.

export interface SupportMember {
   _id: string;
   name: string;
   role: string;
   facebookUrl: string;
   avatarUrl: string;
   order: number;
   active: boolean;
}

export async function listAdminSupportTeamApi(): Promise<{ members: SupportMember[] }> {
   const res = await apiClient.get('/admin/support-team');
   return res.data;
}

export async function createAdminSupportMemberApi(
   body: Partial<SupportMember>
): Promise<{ member: SupportMember }> {
   const res = await apiClient.post('/admin/support-team', body);
   return res.data;
}

export async function updateAdminSupportMemberApi(
   id: string,
   body: Partial<SupportMember>
): Promise<{ member: SupportMember }> {
   const res = await apiClient.put(`/admin/support-team/${id}`, body);
   return res.data;
}

export async function deleteAdminSupportMemberApi(id: string): Promise<{ ok: boolean }> {
   const res = await apiClient.delete(`/admin/support-team/${id}`);
   return res.data;
}

/** The public list — used by the Support popup every operator can open. */
export async function listSupportTeamApi(): Promise<{ members: SupportMember[] }> {
   const res = await apiClient.get('/kb/support-team');
   return res.data;
}

/* ─── Help centre sidebar carousel ──────────────────────────────────── */

export interface SidebarSlide {
   _id: string;
   imageUrl: string;
   linkUrl: string;
   caption: string;
   durationValue: number;
   durationUnit: 'days' | 'months';
   startsAt: string;
   /** Worked out on the server from the duration — never sent up. */
   endsAt: string;
   order: number;
   active: boolean;
}

export async function listAdminSlidesApi(): Promise<{ slides: SidebarSlide[] }> {
   const res = await apiClient.get('/admin/sidebar-slides');
   return res.data;
}

export async function createAdminSlideApi(
   body: Partial<SidebarSlide>
): Promise<{ slide: SidebarSlide }> {
   const res = await apiClient.post('/admin/sidebar-slides', body);
   return res.data;
}

export async function updateAdminSlideApi(
   id: string,
   body: Partial<SidebarSlide>
): Promise<{ slide: SidebarSlide }> {
   const res = await apiClient.put(`/admin/sidebar-slides/${id}`, body);
   return res.data;
}

export async function deleteAdminSlideApi(id: string): Promise<{ ok: boolean }> {
   const res = await apiClient.delete(`/admin/sidebar-slides/${id}`);
   return res.data;
}

/**
 * Read a MikroTik's LAN-only `smartfi-recovery` login.
 *
 * This is the escape hatch for the router lockout: once the setup script pins
 * every login to the WireGuard plane, a dead tunnel means nobody can reach the
 * router — not SmartFi and not the operator — and a factory reset is otherwise
 * the only way back. Platform admins only; the server refuses this for anyone
 * else, and the password is served by this endpoint alone, never in a device
 * list or export.
 *
 * 404 is a normal answer, not an error to shout about: a router whose setup
 * script predates the feature only mints one on its next script generation,
 * and OpenWRT devices never have one.
 */
export async function getRouterRecoveryLoginApi(deviceId: string): Promise<{
   deviceId: string;
   name: string;
   username: string;
   password: string;
   generatedAt: string | null;
   howTo: string;
}> {
   const res = await apiClient.get(
      `/admin/vendo/devices/${deviceId}/recovery-login`
   );
   return res.data;
}

/* ────────────────────────────────────────────────────────────────────────────
 * No-reboot updates ("bundles") — listed on the Admin Panel → Firmware tab under
 * each router board, beside its images (Ray, 2026-09-25: "it should still be
 * listed and activated there"). BUILT by a script in the device repo; listed,
 * noted, canaried and activated here.
 * ──────────────────────────────────────────────────────────────────────────── */

export interface AdminBundle {
   _id: string;
   version: string;
   target: string;
   variant: 'cloud' | 'hybrid';
   fileCount: number;
   totalBytes: number;
   removes: string[];
   notes?: string;
   isActive: boolean;
   uploadedAt: string;
   /** Routers reporting this as their running update / sent it but not yet on it. */
   devicesOnIt?: number;
   devicesArmed?: number;
}

export const getAdminBundles = async (target: string): Promise<AdminBundle[]> => {
   const r = await apiClient.get('/admin/bundle', { params: { target } });
   return r.data.bundles ?? [];
};

/** One zip from the build (smartfi-<board>-<type>-<version>-noreboot.zip), verified server-side. */
export const uploadAdminBundle = async (input: {
   target: string;
   variant: 'cloud' | 'hybrid';
   version: string;
   notes: string;
   file: File;
}, onSentPct?: (pct: number) => void): Promise<{ id: string; version: string; fileCount: number; totalBytes: number }> => {
   const fd = new FormData();
   fd.append('target', input.target);
   fd.append('variant', input.variant);
   fd.append('version', input.version);
   fd.append('notes', input.notes);
   fd.append('file', input.file);
   const r = await apiClient.post('/admin/bundle/upload', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      // A 12 MB package plus the server checking and storing ~70 files can
      // take a couple of minutes; never let a default timeout cut it off.
      timeout: 10 * 60 * 1000,
      onUploadProgress: (e) => {
         if (onSentPct && e.total) onSentPct(Math.min(100, Math.round((e.loaded / e.total) * 100)));
      },
   });
   return r.data;
};

export const activateAdminBundle = async (id: string) =>
   (await apiClient.post(`/admin/bundle/${id}/activate`)).data;

export const deactivateAdminBundle = async (id: string) =>
   (await apiClient.post(`/admin/bundle/${id}/deactivate`)).data;

export const deleteAdminBundle = async (id: string) =>
   (await apiClient.delete(`/admin/bundle/${id}`)).data;

export const updateAdminBundleNotes = async (id: string, notes: string) =>
   (await apiClient.patch(`/admin/bundle/${id}`, { notes })).data;

/** The bundle candidates, in the shape the "Send to…" picker already reads. */
export const getBundleSendCandidates = async (
   id: string
): Promise<{ version: string; variant: 'cloud' | 'hybrid'; devices: FirmwareCandidate[] }> => {
   const r = await apiClient.get(`/admin/bundle/${id}/candidates`);
   const version: string = r.data.version;
   return {
      version,
      variant: r.data.variant,
      devices: (r.data.candidates as any[]).map((c) => ({
         // The send endpoint takes deviceIds, so the picker's id IS the deviceId.
         id: c.deviceId,
         name: c.name,
         deviceId: c.deviceId,
         ownerEmail: c.ownerEmail ?? '',
         ownerName: c.ownerName ?? '',
         runningVersion: c.runningBundle
            ? `${c.runningFirmware} + ${c.runningBundle}`
            : c.runningFirmware,
         variant: r.data.variant,
         platform: 'openwrt',
         lastSeenAt: null,
         eligible: !!c.eligible,
         skipReason: c.eligible ? null : 'too-old',
         skipText: c.eligible ? null : 'Router firmware older than 2.2.0 cannot take a no-reboot update',
         onThisVersion: !!c.alreadyOn,
         armedForThis: !c.alreadyOn && c.pinnedTo === version,
      })),
   };
};

export const sendBundleToRouters = async (
   id: string,
   deviceIds: string[]
): Promise<{
   ok: boolean;
   armed: number;
   summary: string;
   skipped: Array<{ deviceId: string; name: string; reason: string; why: string }>;
}> => {
   const r = await apiClient.post(`/admin/bundle/${id}/send`, { deviceIds });
   const waiting: string[] = r.data.willNotActYet ?? [];
   return {
      ok: !!r.data.ok,
      armed: r.data.sent ?? 0,
      summary: `Sent ${r.data.version} to ${r.data.sent} router${r.data.sent === 1 ? '' : 's'}`,
      skipped: waiting.map((d) => ({
         deviceId: d,
         name: d,
         reason: 'too-old',
         why: 'sent, but its firmware is too old to install it yet',
      })),
   };
};


// ── MikroTik router-settings releases (Firmware → MikroTik router settings) ──
export type ConfigLine = 'cloud' | 'hybrid';
export interface ConfigRelease {
   _id: string;
   version: number;
   /** Which settings line this row is on: Cloud and Hybrid each have their own table + Active. */
   line: ConfigLine;
   notes: string;
   isActive: boolean;
   releasedAt: string;
   activatedAt: string | null;
   activatedBy: string | null;
   routersOnIt: number;
   /** Only the server's current version can be sent: it generates its settings. */
   sendable: boolean;
}

export const listConfigReleases = async (): Promise<{
   codeVersion: number;
   mikrotikRouters: number;
   /** Per line: the version this server's code generates for it, and how many routers are on the line. */
   lines: Array<{ line: ConfigLine; codeVersion: number; routers: number }>;
   releases: ConfigRelease[];
}> => (await apiClient.get('/admin/config-release')).data;

export const updateConfigReleaseNotes = async (id: string, notes: string) =>
   (await apiClient.patch(`/admin/config-release/${id}`, { notes })).data;

export const activateConfigRelease = async (id: string) =>
   (await apiClient.post(`/admin/config-release/${id}/activate`)).data;

export const getConfigReleaseCandidates = async (
   id: string
): Promise<{ version: string; variant: 'cloud' | 'hybrid'; devices: FirmwareCandidate[] }> => {
   const r = await apiClient.get(`/admin/config-release/${id}/candidates`);
   return {
      version: `v${r.data.version}`,
      // Settings fit Cloud and Hybrid MikroTiks alike; nothing is hidden by product.
      variant: 'cloud',
      devices: (r.data.candidates as any[]).map((c) => ({
         id: c.deviceId,
         name: c.name,
         deviceId: c.deviceId,
         ownerEmail: c.ownerEmail ?? '',
         ownerName: c.ownerName ?? '',
         runningVersion: c.runningVersion != null ? `v${c.runningVersion} · ${c.mode}` : `never applied · ${c.mode}`,
         variant: 'cloud',
         platform: 'mikrotik',
         lastSeenAt: null,
         eligible: !!c.eligible,
         skipReason: c.eligible ? null : 'offline',
         skipText: c.eligible ? null : 'Router not reachable right now',
         onThisVersion: !!c.alreadyOn,
         armedForThis: false,
      })),
   };
};

export const sendConfigReleaseToRouters = async (
   id: string,
   deviceIds: string[]
): Promise<{
   ok: boolean;
   armed: number;
   summary: string;
   skipped: Array<{ deviceId: string; name: string; reason: string; why: string }>;
}> => {
   const r = await apiClient.post(`/admin/config-release/${id}/send`, { deviceIds });
   // The server answers as soon as the routers are queued: each router applies
   // the settings on its own (minutes on a big setup) and its row shows the
   // version when its import is done. Waiting here hit the proxy's 90 s cut
   // and showed "Could not send the update" for a send that was running (2026-10-19).
   const failed = (r.data.results as any[]).filter((x) => !x.ok);
   const n = r.data.sent ?? 0;
   return {
      ok: failed.length === 0,
      armed: n,
      summary: `Sending v${r.data.version} to ${n} router${n === 1 ? '' : 's'} — each shows v${r.data.version} here when its import is done (a few minutes on a big setup).`,
      skipped: failed.map((x) => ({ deviceId: x.deviceId, name: x.deviceId, reason: 'failed', why: x.error || 'apply failed' })),
   };
};
